/**
 * スクロールアニメーション - Intersection Observer API を使用
 * パフォーマンス最適化版
 * 参考: https://ics.media/entry/250221/
 *
 * アニメーション順序:
 * 1. .c-global（ページタイトル）
 * 2. .c-global-sub-heading（サブ見出し）
 * 3. .c-global-body（本文）
 * 4. .rich-text__feature-block（コンテンツブロック）
 * 5. .global-footer（フッター）
 */

(function () {
  'use strict';

  /**
   * 設定値
   */
  // SP判定用のブレークポイント
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  const CONFIG = {
    // アニメーション対象のセレクタ（表示順序順）
    // SP時は .c-multicolumn-service__column が display: contents になるため、アイテム単位でアニメーション
    targetSelectors: [
      '.c-global',
      '.c-global-sub-heading',
      '.c-global-body',
      '.cart__items',
      '.cart__footer',
      '.banner',
      '.product__media-wrapper',
      '.product__info-wrapper',
      '.c-product-info-container',
      '.collapsible-content',
      '.c-sitemap__category',
      '.c-contact-form__input',
      // SP時はアイテム単位、PC時はカラム単位でアニメーション
      ...(isMobile ? ['.c-multicolumn-service__item', '.c-multicolumn-service__column > .c-multicolumn-service__border:first-child'] : ['.c-multicolumn-service__column']),
      '.rich-text__feature-block',
      '.rich-text__feature-divider',
      '.c-featured-blog__post',
      '.c-featured-blog__read-all',
      '.instafeed-new-layout-item',
      '.global-footer',
      '.jdgm-rev-widg__title',
      '.jdgm-rev',
    ],
    // 商品ページ内でスキップする親セレクタ（これらの中にあるc-global系要素はスキップ）
    productPageSkipParents: ['.product', '.product__info-wrapper', '.product__info-container'],
    // GSAPで制御するセクション内の要素をスキップ（GSAPと競合を避ける）
    gsapControlledParents: ['.c-brands-text--scroll-animation'],
    // アニメーション準備完了を示すクラス
    readyClass: 'scroll-animation-ready',
    // アクティブ状態を示すクラス
    activeClass: 'isActive',
    // ファーストビューの判定閾値（ビューポート高さの割合）
    firstViewThreshold: 0.9,
    // Intersection ObserverのrootMargin（早めにトリガー）
    rootMargin: '-10% 0px -5%',
    // 要素が見えたと判定する閾値
    threshold: 0.01,
    // ファーストビュー要素の初期遅延（ミリ秒）
    firstViewDelay: 200,
    // 同一セレクタ内の要素間スタッガー遅延（ミリ秒）
    staggerDelay: 150,
    // セレクタ間の遅延
    selectorDelay: 200,
    // コンテンツブロック用のスタッガー遅延（少し短め）
    contentBlockStaggerDelay: 120,
    // フッター用の追加遅延
    footerExtraDelay: 100,
  };

  /**
   * 要素がGSAP制御セクション内にあるかチェック
   * @param {HTMLElement} element チェックする要素
   * @returns {boolean} スキップすべきならtrue
   */
  const shouldSkipForGsap = (element) => {
    for (const parentSelector of CONFIG.gsapControlledParents) {
      if (element.closest(parentSelector)) {
        return true;
      }
    }
    return false;
  };

  /**
   * 要素が商品ページのスキップ対象親要素内にあるかチェック
   * @param {HTMLElement} element チェックする要素
   * @param {string} selector 要素のセレクタ
   * @returns {boolean} スキップすべきならtrue
   */
  const shouldSkipInProductPage = (element, selector) => {
    // GSAP制御セクション内はすべてスキップ
    if (shouldSkipForGsap(element)) {
      return true;
    }

    // c-global系セレクタのみスキップ対象
    const skipSelectors = ['.c-global', '.c-global-sub-heading', '.c-global-body'];
    if (!skipSelectors.includes(selector)) return false;

    // 親要素が商品ページのコンテナかチェック
    for (const parentSelector of CONFIG.productPageSkipParents) {
      if (element.closest(parentSelector)) {
        return true;
      }
    }
    return false;
  };

  /**
   * 要素がビューポート内にあるかチェック
   * @param {HTMLElement} element チェックする要素
   * @returns {boolean} ビューポート内にあれば true
   */
  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < windowHeight && rect.bottom > 0;
  };

  /**
   * 要素がファーストビュー（画面上部）にあるかチェック
   * @param {HTMLElement} element チェックする要素
   * @returns {boolean} ファーストビューにあれば true
   */
  const isInFirstView = (element) => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.top < windowHeight * CONFIG.firstViewThreshold;
  };

  /**
   * 要素をアクティブ化（アニメーション開始）
   * @param {HTMLElement} element 対象要素
   */
  const activateElement = (element) => {
    element.classList.add(CONFIG.activeClass);
  };

  /**
   * Intersection Observerのインスタンスを保持
   */
  const observers = new Map();

  /**
   * 特定のセレクタ用のIntersection Observerを取得または作成
   * @param {string} selector セレクタ
   * @returns {IntersectionObserver}
   */
  const getObserverForSelector = (selector) => {
    if (observers.has(selector)) return observers.get(selector);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activateElement(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        root: null,
        rootMargin: CONFIG.rootMargin,
        threshold: CONFIG.threshold,
      }
    );

    observers.set(selector, observer);
    return observer;
  };

  /**
   * セレクタに応じたスタッガー遅延を取得
   * @param {string} selector セレクタ
   * @returns {number} スタッガー遅延（ミリ秒）
   */
  const getStaggerDelayForSelector = (selector) => {
    if (selector === '.rich-text__feature-block') {
      return CONFIG.contentBlockStaggerDelay;
    }
    if (selector === '.rich-text__feature-divider') {
      return CONFIG.contentBlockStaggerDelay; // 境界線もコンテンツブロックと同様
    }
    if (selector === '.c-featured-blog__post') {
      return CONFIG.contentBlockStaggerDelay; // ブログ投稿もコンテンツブロックと同様
    }
    if (selector === '.instafeed-new-layout-item') {
      return 80; // インスタフィードは行ごとにアニメーション
    }
    if (selector === '.global-footer') {
      return 0; // フッターは1つなのでスタッガー不要
    }
    if (selector === '.c-featured-blog__read-all') {
      return 0; // Read Allボタンは1つなのでスタッガー不要
    }
    // SP時のマルチカラムサービスアイテムは少し短めのスタッガー
    if (selector === '.c-multicolumn-service__item') {
      return 100;
    }
    // SP時のマルチカラムサービスのボーダーは最初だけなのでスタッガー不要
    if (selector === '.c-multicolumn-service__column > .c-multicolumn-service__border:first-child') {
      return 0;
    }
    return CONFIG.staggerDelay;
  };

  /**
   * セレクタに応じた追加遅延を取得
   * @param {string} selector セレクタ
   * @returns {number} 追加遅延（ミリ秒）
   */
  const getExtraDelayForSelector = (selector) => {
    if (selector === '.global-footer') {
      return CONFIG.footerExtraDelay;
    }
    return 0;
  };

  /**
   * 要素のセレクタを取得
   * @param {HTMLElement} element 対象要素
   * @returns {string|null} マッチするセレクタ
   */
  const getSelectorForElement = (element) => {
    for (const selector of CONFIG.targetSelectors) {
      if (element.matches(selector)) {
        return selector;
      }
    }
    return null;
  };

  /**
   * ファーストビュー要素のアニメーションを開始（DOM位置順序）
   * バナーが上にある場合など、ページレイアウトに応じて適切な順序でアニメーション
   * @param {Map<string, HTMLElement[]>} elementsBySelector セレクタごとの要素配列
   */
  const animateFirstViewElements = (elementsBySelector) => {
    // すべてのファーストビュー要素を集めて位置情報を付加
    const allElements = [];

    elementsBySelector.forEach((elements, selector) => {
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        allElements.push({
          element,
          selector,
          top: rect.top,
          left: rect.left,
        });
      });
    });

    // 縦位置（top）でソート、同じ場合は横位置（left）でソート
    allElements.sort((a, b) => {
      if (Math.abs(a.top - b.top) < 10) {
        // ほぼ同じ高さなら左から右へ
        return a.left - b.left;
      }
      return a.top - b.top;
    });

    // グループ化（近い位置にある同じセレクタの要素をまとめる）
    const groups = [];
    let currentGroup = null;

    allElements.forEach((item) => {
      const shouldStartNewGroup = !currentGroup || currentGroup.selector !== item.selector || Math.abs(currentGroup.lastTop - item.top) > 100; // 100px以上離れていたら別グループ

      if (shouldStartNewGroup) {
        currentGroup = {
          selector: item.selector,
          elements: [item.element],
          lastTop: item.top,
        };
        groups.push(currentGroup);
      } else {
        currentGroup.elements.push(item.element);
        currentGroup.lastTop = item.top;
      }
    });

    // グループごとに遅延を計算してアニメーション
    let totalDelay = CONFIG.firstViewDelay;

    groups.forEach((group) => {
      const staggerDelay = getStaggerDelayForSelector(group.selector);
      const extraDelay = getExtraDelayForSelector(group.selector);

      group.elements.forEach((element, index) => {
        const delay = totalDelay + extraDelay + index * staggerDelay;
        setTimeout(() => {
          activateElement(element);
        }, delay);
      });

      // 次のグループへの遅延を追加
      totalDelay += group.elements.length * staggerDelay + CONFIG.selectorDelay + extraDelay;
    });
  };

  /**
   * スクロール対象要素のアニメーションを設定
   * @param {Map<string, HTMLElement[]>} elementsBySelector セレクタごとの要素配列
   */
  const setupScrollElements = (elementsBySelector) => {
    CONFIG.targetSelectors.forEach((selector) => {
      const elements = elementsBySelector.get(selector) || [];
      const observer = getObserverForSelector(selector);

      elements.forEach((element) => {
        observer.observe(element);
      });
    });
  };

  /**
   * スクロールアニメーションの初期化
   */
  const initScrollAnimation = () => {
    // モーション削減設定をチェック
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      CONFIG.targetSelectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((target) => {
          // 商品ページ内のc-global系要素はスキップ
          if (!shouldSkipInProductPage(target, selector)) {
            target.classList.add(CONFIG.activeClass);
          }
        });
      });
      document.documentElement.classList.add(CONFIG.readyClass);
      return;
    }

    // 要素をセレクタごと、ファーストビュー/スクロールで分類
    const firstViewBySelector = new Map();
    const scrollBySelector = new Map();

    CONFIG.targetSelectors.forEach((selector) => {
      const firstViewElements = [];
      const scrollElements = [];

      document.querySelectorAll(selector).forEach((target) => {
        // 商品ページ内のc-global系要素はスキップ
        if (shouldSkipInProductPage(target, selector)) {
          return;
        }

        if (isInViewport(target) && isInFirstView(target)) {
          firstViewElements.push(target);
        } else {
          scrollElements.push(target);
        }
      });

      if (firstViewElements.length > 0) {
        firstViewBySelector.set(selector, firstViewElements);
      }
      if (scrollElements.length > 0) {
        scrollBySelector.set(selector, scrollElements);
      }
    });

    // 準備完了クラスを追加
    document.documentElement.classList.add(CONFIG.readyClass);

    // ファーストビュー要素のアニメーション開始（順序制御）
    if (firstViewBySelector.size > 0) {
      animateFirstViewElements(firstViewBySelector);
    }

    // スクロール対象要素の監視開始
    if (scrollBySelector.size > 0) {
      setupScrollElements(scrollBySelector);
    }
  };

  /**
   * Judge.me レビュー用の動的監視
   * 外部ウィジェットのため、遅延ロードに対応
   */
  const observeJudgemeReviews = () => {
    const reviewsContainer = document.querySelector('.jdgm-rev-widg__reviews');
    if (!reviewsContainer) return;

    const observer = getObserverForSelector('.jdgm-rev');

    // 既存のレビューを監視
    reviewsContainer.querySelectorAll('.jdgm-rev:not(.isActive)').forEach((review) => {
      if (!review.dataset.scrollObserved) {
        review.dataset.scrollObserved = 'true';
        observer.observe(review);
      }
    });

    // MutationObserverで新しいレビュー（ページネーション等）を監視
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('jdgm-rev')) {
              if (!node.dataset.scrollObserved) {
                node.dataset.scrollObserved = 'true';
                observer.observe(node);
              }
            }
            // 子要素内のレビューも検索
            const nestedReviews = node.querySelectorAll ? node.querySelectorAll('.jdgm-rev:not([data-scroll-observed])') : [];
            nestedReviews.forEach((review) => {
              review.dataset.scrollObserved = 'true';
              observer.observe(review);
            });
          }
        });
      });
    });

    mutationObserver.observe(reviewsContainer, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Judge.meウィジェットの遅延読み込み対応
   */
  const initJudgemeObserver = () => {
    // 既にレビューコンテナがあれば即座に監視開始
    if (document.querySelector('.jdgm-rev-widg__reviews')) {
      observeJudgemeReviews();
      return;
    }

    // レビューコンテナがない場合はMutationObserverで待機
    const bodyObserver = new MutationObserver((mutations, obs) => {
      if (document.querySelector('.jdgm-rev-widg__reviews')) {
        observeJudgemeReviews();
        obs.disconnect();
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // 10秒後にタイムアウト
    setTimeout(() => {
      bodyObserver.disconnect();
    }, 10000);
  };

  /**
   * Instafeed 用の動的監視
   * 外部アプリのため、遅延ロードに対応
   */
  const observeInstafeedItems = () => {
    const instafeedContainer = document.querySelector('.instafeed-new-layout-wrapper');
    if (!instafeedContainer) return;

    const observer = getObserverForSelector('.instafeed-new-layout-item');

    // 既存のアイテムを監視
    instafeedContainer.querySelectorAll('.instafeed-new-layout-item:not(.isActive)').forEach((item) => {
      if (!item.dataset.scrollObserved) {
        item.dataset.scrollObserved = 'true';
        observer.observe(item);
      }
    });

    // MutationObserverで新しいアイテムを監視
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.classList && node.classList.contains('instafeed-new-layout-item')) {
              if (!node.dataset.scrollObserved) {
                node.dataset.scrollObserved = 'true';
                observer.observe(node);
              }
            }
            // 子要素内のアイテムも検索
            const nestedItems = node.querySelectorAll ? node.querySelectorAll('.instafeed-new-layout-item:not([data-scroll-observed])') : [];
            nestedItems.forEach((item) => {
              item.dataset.scrollObserved = 'true';
              observer.observe(item);
            });
          }
        });
      });
    });

    mutationObserver.observe(instafeedContainer, {
      childList: true,
      subtree: true,
    });
  };

  /**
   * Instafeedアプリの遅延読み込み対応
   */
  const initInstafeedObserver = () => {
    // 既にコンテナがあれば即座に監視開始
    if (document.querySelector('.instafeed-new-layout-wrapper')) {
      observeInstafeedItems();
      return;
    }

    // コンテナがない場合はMutationObserverで待機
    const bodyObserver = new MutationObserver((mutations, obs) => {
      if (document.querySelector('.instafeed-new-layout-wrapper')) {
        observeInstafeedItems();
        obs.disconnect();
      }
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // 10秒後にタイムアウト
    setTimeout(() => {
      bodyObserver.disconnect();
    }, 10000);
  };

  /**
   * ページ読み込み完了を待って初期化
   */
  const init = () => {
    if (document.readyState === 'complete') {
      setTimeout(() => {
        initScrollAnimation();
        initJudgemeObserver();
        initInstafeedObserver();
      }, 100);
    } else {
      window.addEventListener('load', () => {
        setTimeout(() => {
          initScrollAnimation();
          initJudgemeObserver();
          initInstafeedObserver();
        }, 100);
      });
    }
  };

  init();
})();
